import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faDownload, 
  faDesktop, 
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { useAppConfig } from '../../AppConfigProvider';
import './DownloadManager.css';

export default function DownloadManager() {
  const appConfig = useAppConfig();
  const downloadUrl = `${appConfig.filesHost}docusync_manager/DocuSyncManager.exe`;

  return (
    <div className="container download-manager-container">
      <div className="page-header">
        <h1 className="page-title">
          <FontAwesomeIcon icon={faDesktop} />
          DocuSync Manager Setup & Guide
        </h1>
        <p className="page-subtitle">Configure your system and sync your bills to QuickBooks Desktop</p>
      </div>

      <div className="row g-4 justify-content-center">
        {/* Main Download Card */}
        <div className="col-12 col-lg-10">
          <div className="card download-card shadow-sm border-0 rounded-4 mb-4">
            <div className="card-body p-4 p-md-5 text-center">
              <div className="download-icon-wrapper mb-4">
                <FontAwesomeIcon icon={faDownload} className="download-hero-icon" />
              </div>
              <h2 className="fw-bold mb-3" style={{ color: '#1e293b' }}>Download Sync Client</h2>
              <p className="text-muted mx-auto mb-4" style={{ maxWidth: '600px', fontSize: '1.05rem' }}>
                The DocuSync Manager runs locally on your Windows machine to securely connect your QuickBooks Desktop database with your DocuSync web dashboard.
              </p>
              
              <div className="d-flex flex-column align-items-center gap-3">
                <a 
                  href={downloadUrl} 
                  className="btn-premium success shadow-sm px-5 py-3 rounded-pill fw-bold"
                  style={{ fontSize: '1.1rem', minWidth: '280px' }}
                  download
                >
                  <FontAwesomeIcon icon={faDownload} />
                  Download DocuSync Manager
                </a>
                
                <div className="d-flex align-items-center gap-4 text-muted mt-2" style={{ fontSize: '0.9rem' }}>
                  <span><strong>Platform:</strong> Windows 10 / 11</span>
                  <span className="dot-divider">•</span>
                  <span><strong>Prerequisite:</strong> QuickBooks Desktop</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Step-by-Step Guidelines */}
        <div className="col-12 col-lg-10">
          <div className="card guide-card shadow-sm border-0 rounded-4 mb-4">
            <div className="card-body p-4 p-md-5">
              <h3 className="fw-bold text-dark mb-4 text-center">Setup & Sync Guidelines</h3>
              
              <div className="steps-timeline">
                {/* Step 1 */}
                <div className="step-item">
                  <div className="step-number-container">
                    <span className="step-number">1</span>
                  </div>
                  <div className="step-content">
                    <h5 className="fw-bold text-dark mb-2">Start QuickBooks Desktop</h5>
                    <p className="text-muted mb-3">
                      Before running the DocuSync Manager, open your QuickBooks software on the local system. 
                      Log in to the correct company file and leave QuickBooks open.
                    </p>
                    <div className="alert alert-warning d-flex align-items-center gap-2 p-3 border-0 rounded-3 mb-3" style={{ fontSize: '0.87rem', background: '#fffbeb', color: '#b45309' }}>
                      <FontAwesomeIcon icon={faInfoCircle} className="fs-6 flex-shrink-0" />
                      <span>QuickBooks must be running first, otherwise the manager cannot initialize the connection.</span>
                    </div>
                    <div className="step-image-wrapper">
                      <img src="/quickbook.png" alt="QuickBooks Running" className="step-image img-fluid rounded-3 border" />
                    </div>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="step-item">
                  <div className="step-number-container">
                    <span className="step-number">2</span>
                  </div>
                  <div className="step-content">
                    <h5 className="fw-bold text-dark mb-2">Launch DocuSync Manager</h5>
                    <p className="text-muted mb-3">
                      Locate the downloaded <strong>DocuSyncManager.exe</strong> and run it. 
                      Verify that the status panel displays <strong className="text-success">System Ready & Running</strong> with a green shield. 
                      This indicates it is securely paired with your active QuickBooks company file.
                    </p>
                    <div className="step-image-wrapper">
                      <img src="/docusyncmanager.png" alt="DocuSync Manager Running" className="step-image img-fluid rounded-3 border" />
                    </div>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="step-item">
                  <div className="step-number-container">
                    <span className="step-number">3</span>
                  </div>
                  <div className="step-content">
                    <h5 className="fw-bold text-dark mb-2">Push Bills from Web Dashboard</h5>
                    <p className="text-muted mb-3">
                      With both QuickBooks and DocuSync Manager open and active, navigate to the web dashboard's 
                      <strong> Push Bills</strong> screen. Click <strong>Publish to QB</strong> or <strong>Publish All Pending Bills</strong> to transmit your bills seamlessly.
                    </p>
                    <div className="step-image-wrapper">
                      <img src="/pushWindow.png" alt="Push Bills Window" className="step-image img-fluid rounded-3 border" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
